const GA_ID = process.env.GOOGLE_ANALYTICS_ID;
const META_PIXEL_ID = process.env.META_PIXEL_ID;

const CURRENCY = 'ZAR';

let initialized = false;
let metaInitialized = false;
let skipNextMetaPageView = false;

const hasGA = () => GA_ID && typeof window.gtag === 'function';
const hasMeta = () => META_PIXEL_ID && typeof window.fbq === 'function';

// Injects the gtag.js script into <head> once. Page views are sent manually
// via trackPageView so SPA route changes are counted exactly once.
export const initGA = () => {
  if (initialized || !GA_ID || typeof window === 'undefined') return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, { send_page_view: false });
};

// Injects the Meta Pixel base code into <head> once and fires the first PageView.
export const initMetaPixel = () => {
  if (metaInitialized || !META_PIXEL_ID || typeof window === 'undefined') return;
  metaInitialized = true;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
  // The base PageView above already counts the current route, so the very next
  // trackPageView call (initial route) must not fire a duplicate Meta PageView.
  skipNextMetaPageView = true;
};

export const trackPageView = (path) => {
  if (hasGA()) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  // The Meta base code already fires the first PageView on init; this tracks
  // subsequent SPA route changes.
  if (hasMeta() && metaInitialized) {
    if (skipNextMetaPageView) {
      skipNextMetaPageView = false;
    } else {
      window.fbq('track', 'PageView');
    }
  }
};

// --- Ecommerce funnel ---------------------------------------------------------
// item: { reference_number, name, price }
// items[]: [{ reference_number, plan_name, total_price }]

const gaItem = (it) => ({
  item_id: it.reference_number || '',
  item_name: it.plan_name || it.name || '',
  price: Number(it.total_price ?? it.price) || 0,
  quantity: 1,
});

const metaContents = (items) =>
  items.map((it) => ({
    id: it.reference_number || '',
    quantity: 1,
    item_price: Number(it.total_price ?? it.price) || 0,
  }));

const metaContentIds = (items) => items.map((it) => it.reference_number || '');

export const trackViewItem = (item) => {
  if (!item) return;
  const price = Number(item.price) || 0;
  if (hasGA()) {
    window.gtag('event', 'view_item', {
      currency: CURRENCY,
      value: price,
      items: [gaItem(item)],
    });
  }
  if (hasMeta()) {
    window.fbq('track', 'ViewContent', {
      content_type: 'product',
      content_ids: [item.reference_number || ''],
      content_name: item.name || '',
      value: price,
      currency: CURRENCY,
    });
  }
};

export const trackAddToCart = (item) => {
  if (!item) return;
  const price = Number(item.price) || 0;
  if (hasGA()) {
    window.gtag('event', 'add_to_cart', {
      currency: CURRENCY,
      value: price,
      items: [gaItem(item)],
    });
  }
  if (hasMeta()) {
    window.fbq('track', 'AddToCart', {
      content_type: 'product',
      content_ids: [item.reference_number || ''],
      content_name: item.name || '',
      value: price,
      currency: CURRENCY,
    });
  }
};

export const trackBeginCheckout = (items, total) => {
  const list = items || [];
  const value = Number(total) || 0;
  if (hasGA()) {
    window.gtag('event', 'begin_checkout', {
      currency: CURRENCY,
      value,
      items: list.map(gaItem),
    });
  }
  if (hasMeta()) {
    window.fbq('track', 'InitiateCheckout', {
      content_type: 'product',
      content_ids: metaContentIds(list),
      contents: metaContents(list),
      num_items: list.length,
      value,
      currency: CURRENCY,
    });
  }
};

// --- Registration funnel (no PII: only method + source label) ---------------
// source: 'from_purchase' | 'direct'

export const trackSignUpStart = (source) => {
  if (hasGA()) {
    window.gtag('event', 'sign_up_start', { method: 'email', source });
  }
  if (hasMeta()) {
    window.fbq('trackCustom', 'StartRegistration', { source });
  }
};

export const trackSignUp = (source, method = 'email') => {
  if (hasGA()) {
    window.gtag('event', 'sign_up', { method, source });
  }
  if (hasMeta()) {
    window.fbq('track', 'CompleteRegistration', { status: true, source });
  }
};

export const trackPurchase = (order) => {
  if (!order) return;
  const items = order.items || [];
  const value = Number(order.total_amount) || 0;
  if (hasGA()) {
    window.gtag('event', 'purchase', {
      transaction_id: order.order_id,
      value,
      currency: CURRENCY,
      items: items.map(gaItem),
    });
  }
  if (hasMeta()) {
    window.fbq('track', 'Purchase', {
      content_type: 'product',
      content_ids: metaContentIds(items),
      contents: metaContents(items),
      num_items: items.length,
      value,
      currency: CURRENCY,
    });
  }
};
